import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button, Modal, toast, useOverlayState } from "@heroui/react";
import { Share2, UserPlus } from "lucide-react";
import { useCallback, useEffect, useMemo } from "react";
import type { MyBlacklistUser } from "@repo/api/blacklist.type";
import { useAuth } from "@repo/context/auth/auth";
import { useTemplateProtectionGuidelines } from "@repo/context/template-protection-guidelines";
import { useTemplateStats } from "@repo/query";
import { AddBlacklistUserForm } from "@repo/ui/pages/template_service/blacklist/AddBlacklistUserForm";
import { EditEvidenceButton } from "@repo/ui/pages/template_service/blacklist/EditBlacklistEntryForm";
import { BlacklistUserListItem } from "@repo/ui/pages/template_service/blacklist/BlacklistUserListItem";
import { RemoveBlacklistButton } from "@repo/ui/pages/template_service/blacklist/RemoveBlacklistButton";
import { ShareBlacklistSettingsForm } from "@repo/ui/pages/template_service/blacklist/ShareBlacklistSettingsForm";
import { useMyBlacklistInfinite } from "@repo/ui/pages/template_service/blacklist/use-blacklist";
import { appConfig } from "@/config/app-config";
import { InfiniteScroll } from "@/components/infinite-scroll";
import { createBlacklistEvidenceUploader } from "./-components/uploadBlacklistEvidence";

export const Route = createFileRoute("/template_protections/blacklist/self")({
  component: MyBlacklistPage,
});

const PAGE_SIZE = 20;

export default function MyBlacklistPage() {
  const navigate = useNavigate();
  const { userInfo, isAuthenticated } = useAuth();
  const { hasAgreedToGuidelines } = useTemplateProtectionGuidelines();
  const { data: statusData, isLoading: statusLoading } = useTemplateStats(
    userInfo?.uid,
    0,
  );
  const shareLimit = appConfig.tplService.blackListMinSendRequired;
  const canShare = (statusData?.send_count ?? 0) >= shareLimit;

  const addModal = useOverlayState();
  const shareModal = useOverlayState();

  const uploadFile = useMemo(
    () =>
      userInfo?.uid
        ? createBlacklistEvidenceUploader(userInfo.uid)
        : async () => {
          throw new Error("未登录");
        },
    [userInfo?.uid],
  );

  const enabled = isAuthenticated === true;
  const {
    data,
    error,
    isPending,
    hasNextPage,
    isFetchingNextPage: loadingMore,
    fetchNextPage,
  } = useMyBlacklistInfinite(PAGE_SIZE, enabled);
  const list: MyBlacklistUser[] = data?.pages.flatMap((p) => p.list) ?? [];
  // 未登录时不请求，isPending 会一直为 true，这里按非加载态处理
  const loading = enabled && isPending;
  const hasMore = hasNextPage ?? false;

  useEffect(() => {
    if (error) {
      toast.danger((error as { msg?: string }).msg || "加载失败");
    }
  }, [error]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    void fetchNextPage();
  }, [loading, loadingMore, hasMore, fetchNextPage]);

  if (!isAuthenticated) {
    return (
      <div className="py-12 text-center text-gray-500 text-sm px-4">
        请重新从 App 打开本页面
      </div>
    );
  }

  return (
    <div>
      <div className="px-4 pt-3 flex flex-col gap-3">
        <div className="flex gap-2 flex-wrap justify-between">
          <Button
            variant="tertiary"
            size="sm"
            className="rounded-full"
            isDisabled={statusLoading || loading || !canShare || !hasAgreedToGuidelines}
            onPress={shareModal.open}
          >
            <Share2 size={16} />
            {statusData != null
              ? canShare
                ? "共享名单"
                : `达到${shareLimit}发货数时可用`
              : "共享暂不可用"}
          </Button>
          <Button
            variant="primary"
            size="sm"
            className="rounded-full"
            isDisabled={!hasAgreedToGuidelines || loading}
            onPress={addModal.open}
          >
            <UserPlus size={16} />
            添加用户
          </Button>
        </div>

        <InfiniteScroll
          loading={loadingMore || loading}
          hasMore={hasMore}
          onLoadMore={loadMore}
          loadingText="加载中"
          noMoreText="没有更多了"
        >
          {list.length === 0 && !loading ? (
            <div className="py-12 text-center text-gray-500 text-sm">
              暂无黑名单用户
            </div>
          ) : (
            list.map((item) => (
              <BlacklistUserListItem
                key={item.uid}
                item={item}
                onPress={() =>
                  navigate({
                    to: "/template_protections/trusted-query",
                    search: { search: item.uid },
                  })
                }
                actions={
                  <div className="flex flex-col gap-1 items-end">
                    <EditEvidenceButton
                      item={item}
                      uploadFile={uploadFile}
                      cdnBase={appConfig.cos.mainCDN}
                      isDisabled={!hasAgreedToGuidelines}
                    />
                    <RemoveBlacklistButton
                      uid={item.uid}
                      isDisabled={!hasAgreedToGuidelines}
                    />
                  </div>
                }
              />
            ))
          )}
        </InfiniteScroll>
      </div>

      <Modal.Backdrop isOpen={addModal.isOpen} onOpenChange={addModal.setOpen}>
        <Modal.Container size="lg" scroll="inside">
          <Modal.Dialog>
            {({ close }) => (
              <>
                <Modal.Header>
                  <Modal.Heading>添加用户到黑名单</Modal.Heading>
                </Modal.Header>
                <Modal.Body>
                  <AddBlacklistUserForm
                    uploadFile={uploadFile}
                    isDisabled={!hasAgreedToGuidelines}
                    onCancel={close}
                    onSuccess={close}
                  />
                </Modal.Body>
              </>
            )}
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>

      <Modal.Backdrop
        isOpen={shareModal.isOpen}
        onOpenChange={shareModal.setOpen}
      >
        <Modal.Container size="lg" scroll="inside">
          <Modal.Dialog>
            {({ close }) => (
              <>
                <Modal.Header>
                  <Modal.Heading>共享黑名单</Modal.Heading>
                </Modal.Header>
                <Modal.Body>
                  <ShareBlacklistSettingsForm
                    onCancel={close}
                    onSuccess={() => {
                      close();
                    }}
                  />
                </Modal.Body>
              </>
            )}
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </div>
  );
}
